import {
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addToFavorites,
  getMyListings,
  removeFromFavorites,
} from "../../../services/api";
import {
  pushUserFavoritesData,
  setUserFavoritesData,
} from "../../../store/reducers/listingReducer";
import { formatDate } from "../../../helpers/util";
import { Table } from "../../ui/Table";
import { PaginationComponent } from "../../ui/Pagination";
import { ReactComponent as Heart } from "../../../assets/svg/heart.svg";
import { ReactComponent as HeartFilled } from "../../../assets/svg/heartFilled.svg";
import { Link } from "react-router-dom";
import { baseQueryAPI } from "../../../services/baseQuery";

interface Lead {
  name: string;
  secondary_name: string;
  phone: string;
  address: string;
  state: string;
  zip_code: string;
  lead_status: string;
  time_listed: number;
  lead_owner: string;
  lead_notes: string;
  lead_status_options_public_id: string;
  listing_unique_id: string;
  batch_data: string;
}

export const LeadsPanel = ({
  activeIndex,
  search,
  filters
}: {
  activeIndex: number;
  search: string;
  filters: any
}) => {
  const dispatch = useDispatch();
  const logged = useSelector((state: any) => state.listingReducer.logged);
  const userFavorites = useSelector(
    (state: any) => state.listingReducer.userFavorites
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    setColumnFilters([{ id: "name", value: search }]);
  }, [search]);

  useEffect(() => {
    setCurrentPage(0);
  }, [activeIndex]);

  const { data: myListing } = baseQueryAPI.useManageLeadsQuery(
    {
      leadType: activeIndex.toString(),
      currentPage: currentPage + 1,
      filters
    },
    {
      skip: !logged,
    }
  );

  useEffect(() => {
    if (myListing) {
      setLeads(myListing.data);
      setTotalItems(myListing.total || myListing.data.length);
    }
  }, [myListing]);

  const isFavorited = (listing: any) => {
    for (const fav of userFavorites) {
      if (fav.listing_unique_id === listing.listing_unique_id) {
        return true;
      }
    }
    return false;
  };

  const toggleFavorite = async (listing: any) => {
    const params = { listing_unique_id: listing.listing_unique_id };

    if (isFavorited(listing)) {
      const response = await removeFromFavorites(params);
      if (!response?.data.success) {
        return;
      }

      const newFavorites = userFavorites.filter(
        (itm: any) => itm.listing_unique_id !== listing.listing_unique_id
      );
      dispatch(setUserFavoritesData(newFavorites));
    } else {
      const response = await addToFavorites(params);
      if (!response?.data.success) {
        return;
      }
      dispatch(pushUserFavoritesData(response.data.data));
    }
  };

  const columnHelper = createColumnHelper<Lead>();
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "fav",
        cell(props) {
          return (
            <button onClick={() => toggleFavorite(props.row.original)}>
              {isFavorited(props.row.original) ? <HeartFilled /> : <Heart />}
            </button>
          );
        },
      }),
      columnHelper.accessor("name", {
        cell: (info) => info.getValue(),
        header: "Name",
      }),
      columnHelper.accessor("time_listed", {
        cell: (info) => formatDate(info.getValue()),
        header: "Created Date",
      }),
      columnHelper.accessor("address", {
        cell: (info) => info.getValue(),
        header: "Address",
      }),
      columnHelper.accessor("lead_owner", {
        cell: (info) => info.getValue(),
        header: "Lead Owner",
      }),
      columnHelper.accessor("state", {
        cell: (info) => info.getValue(),
        header: "State",
      }),
      columnHelper.accessor("zip_code", {
        cell: (info) => info.getValue(),
        header: "Zip Code",
      }),
      columnHelper.accessor("phone", {
        cell: (info) => info.getValue(),
        header: "Phone",
      }),
      columnHelper.accessor("lead_status", {
        cell: (info) => info.getValue(),
        header: "Status",
      }),
      columnHelper.accessor("lead_notes", {
        cell: (info) => info.getValue(),
        header: "Notes",
      }),
      columnHelper.display({
        id: "view",
        cell(props) {
          return (
            <Link
              to={`/${props.row.original.listing_unique_id}`}
              className="text-primary"
            >
              View
            </Link>
          );
        },
      }),
    ],
    [columnHelper]
  );

  const table = useReactTable({
    columns,
    data: leads,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      columnFilters,
    },
  });

  return (
    <div className="mt-6">
      <Table heading={table.getHeaderGroups()} body={table.getRowModel()} />

      <div>
        <PaginationComponent
          currentPage={currentPage}
          totalPages={Math.ceil(totalItems / 10)}
          setCurrentPage={(page) => setCurrentPage(page)}
        />
      </div>
    </div>
  );
};
